import { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatTimeRange } from "@/lib/format";
import WhatsappGateModal from "@/components/WhatsappGateModal";
import AddConfirmedModal from "@/components/AddConfirmedModal";
import { useMessageChoice } from "@/components/MessageChoiceDialog";
import { Plus, Pencil, Trash2, Copy, X, Check, Clock, Send, AlertTriangle, Loader2, Search, UserPlus, Link2 } from "lucide-react";

const SHIFT_STATUS = [
  ["nao_contactado", "Não contactado"],
  ["contactado", "Contactado"],
  ["disponivel", "Disponível"],
  ["indisponivel", "Recusou"],
  ["confirmado", "Confirmado"],
  ["dispensado", "Dispensado"],
];
const STATUS_LABEL = Object.fromEntries(SHIFT_STATUS);
const STATUS_CLS = {
  nao_contactado: "bg-zinc-100 text-zinc-600",
  contactado: "bg-blue-100 text-blue-700",
  disponivel: "bg-emerald-100 text-emerald-700",
  indisponivel: "bg-red-100 text-red-700",
  confirmado: "bg-emerald-600 text-white",
  dispensado: "bg-zinc-200 text-zinc-500 line-through",
};

const toMin = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  return h * 60 + (m || 0);
};
const overlaps = (aS, aE, bS, bE) => {
  const a1 = toMin(aS), a2 = toMin(aE), b1 = toMin(bS), b2 = toMin(bE);
  if (a1 == null || a2 == null || b1 == null || b2 == null) return false;
  return a1 < b2 && b1 < a2;
};

const byStatus = (shift, st) => shift.candidates.filter((c) => c.status === st).map((c) => c.promoter_id);

function ShiftList({ title, ids, promoterMap, primary, secondary, tertiary, testid, onAdd, overlapMap }) {
  return (
    <div className="border border-zinc-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2.5 bg-zinc-50/50 border-b border-zinc-100 flex items-center justify-between">
        <span className="text-sm font-semibold text-zinc-800">{title}</span>
        <div className="flex items-center gap-2">
          {onAdd && (
            <button onClick={onAdd} data-testid={`${testid}-add`} title="Adicionar confirmado diretamente" className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white">
              <UserPlus className="w-3 h-3" /> Adicionar
            </button>
          )}
          <span className="text-xs text-zinc-500">{ids.length}</span>
        </div>
      </div>
      <div className="divide-y divide-zinc-100 max-h-64 overflow-y-auto">
        {ids.length === 0 && <div className="px-4 py-6 text-center text-xs text-zinc-400">Sem promotores</div>}
        {ids.map((pid) => (
          <div key={pid} className="flex items-center gap-2 px-3 py-2">
            <span className="flex-1 text-sm text-zinc-800 truncate">{promoterMap[pid]?.name || pid.slice(0, 6)}</span>
            {overlapMap && overlapMap[pid] && overlapMap[pid].length > 0 && (
              <span
                data-testid={`overlap-warning-${pid}`}
                title={`Horário sobreposto: ${overlapMap[pid].map((c) => `${c.event_name} (${c.time}) — ${c.status === "confirmado" ? "confirmado" : "aceitou"}`).join(" · ")}`}
                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200 whitespace-nowrap"
              >
                <AlertTriangle className="w-3 h-3" /> sobreposto
              </span>
            )}
            {tertiary && <button onClick={() => tertiary.onClick(pid)} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded border border-zinc-200 text-zinc-600 hover:bg-zinc-50">{tertiary.Icon && <tertiary.Icon className="w-3 h-3" />} {tertiary.label}</button>}
            {secondary && <button onClick={() => secondary.onClick(pid)} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50">{secondary.Icon && <secondary.Icon className="w-3 h-3" />} {secondary.label}</button>}
            {primary && <button onClick={() => primary.onClick(pid)} className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded ${primary.cls}`}>{primary.Icon && <primary.Icon className="w-3 h-3" />} {primary.label}</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ShiftsPanel({ eventId, canEdit, onTotals }) {
  const [msgDialog, askMessage] = useMessageChoice();
  const [shifts, setShifts] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", start_time: "", end_time: "", capacity: 1 });
  const [addSearch, setAddSearch] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sendModal, setSendModal] = useState(null);
  const [showWaGate, setShowWaGate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [addShift, setAddShift] = useState(null);
  const [message, setMessage] = useState("");
  const [overlapMap, setOverlapMap] = useState({});

  const promoterMap = useMemo(() => Object.fromEntries(promoters.map((p) => [p.id, p])), [promoters]);

  const loadShifts = useCallback(() => {
    api.get(`/events/${eventId}/shifts`).then((r) => {
      setShifts(r.data);
      if (onTotals) {
        const confirmedPromoterIds = [...new Set(
          r.data.flatMap((s) => (s.candidates || []).filter((c) => c.status === "confirmado").map((c) => c.promoter_id)),
        )];
        onTotals({
          capacity: r.data.reduce((a, s) => a + (s.capacity || 0), 0),
          confirmed: r.data.reduce((a, s) => a + (s.confirmed_count || 0), 0),
          count: r.data.length,
          confirmedPromoterIds,
        });
      }
    }).catch(() => setShifts([]));
    api.get(`/events/${eventId}/overlap-warnings`).then((r) => setOverlapMap(r.data?.shifts || {})).catch(() => setOverlapMap({}));
  }, [eventId, onTotals]);

  useEffect(() => {
    loadShifts();
    api.get("/promoters").then((r) => setPromoters(r.data)).catch(() => setPromoters([]));
    api.get(`/events/${eventId}/recommendations`).then((r) => setRecommendations(r.data)).catch(() => setRecommendations([]));
    api.get(`/templates?channel=whatsapp`).then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  }, [loadShifts, eventId]);

  // Auto-refresh shift candidates so responses appear without reloading.
  useEffect(() => {
    const iv = setInterval(loadShifts, 15000);
    return () => clearInterval(iv);
  }, [loadShifts]);

  const busyMap = useMemo(() => Object.fromEntries(recommendations.map((r) => [r.id, r.busy_same_day ? (r.busy_same_day_events || []) : null])), [recommendations]);
  const toggleSel = (sid, pid) => {
    const cur = new Set(selected[sid] || []);
    cur.has(pid) ? cur.delete(pid) : cur.add(pid);
    setSelected({ ...selected, [sid]: [...cur] });
  };
  const openSend = (shift) => {
    const ids = selected[shift.id] || [];
    if (ids.length === 0) return;
    setSelectedTemplate("");
    setMessage("");
    setSendModal(shift);
  };
  const applyTemplate = (tplId) => {
    setSelectedTemplate(tplId);
    if (!tplId) return;
    const tpl = templates.find((t) => t.id === tplId);
    if (tpl) setMessage(tpl.body);
  };
  const doSend = async () => {
    const shift = sendModal;
    if (!shift) return;
    const ids = selected[shift.id] || [];
    setSending(true);
    try {
      const payload = { promoter_ids: ids, message };
      if (selectedTemplate) payload.template_id = selectedTemplate;
      const { data } = await api.post(`/events/${eventId}/shifts/${shift.id}/requests`, payload);
      if (data.whatsapp_enabled === false) {
        setSendModal(null);
        setShowWaGate(true);
        return;
      }
      setSelected({ ...selected, [shift.id]: [] });
      setSendModal(null);
      loadShifts();
    } finally { setSending(false); }
  };

  const resetForm = () => { setForm({ name: "", start_time: "", end_time: "", capacity: 1 }); setEditing(null); setShowForm(false); };

  const submitShift = async () => {
    if (!form.name.trim()) { window.alert("Dá um nome ao turno."); return; }
    const payload = { name: form.name.trim(), start_time: form.start_time || null, end_time: form.end_time || null, capacity: parseInt(form.capacity) || 1 };
    if (editing) await api.put(`/events/${eventId}/shifts/${editing}`, payload);
    else await api.post(`/events/${eventId}/shifts`, payload);
    resetForm();
    loadShifts();
  };

  const editShift = (s) => { setEditing(s.id); setForm({ name: s.name, start_time: s.start_time || "", end_time: s.end_time || "", capacity: s.capacity }); setShowForm(true); };
  const deleteShift = async (s) => { if (!window.confirm(`Eliminar o turno "${s.name}"?`)) return; await api.delete(`/events/${eventId}/shifts/${s.id}`); if (activeTab === s.id) setActiveTab("all"); loadShifts(); };
  const duplicateShift = async (s) => { await api.post(`/events/${eventId}/shifts/${s.id}/duplicate`); loadShifts(); };

  const setStatus = async (shift, pid, status) => {
    if (status === "confirmado") {
      const conflict = shifts.find((o) => o.id !== shift.id && o.candidates.some((c) => c.promoter_id === pid && c.status === "confirmado") && overlaps(shift.start_time, shift.end_time, o.start_time, o.end_time));
      if (conflict && !window.confirm(`Esta pessoa já está confirmada num turno com horário sobreposto: ${conflict.name} (${formatTimeRange(conflict.start_time, conflict.end_time)}). Confirmar mesmo assim?`)) return;
      const cross = (overlapMap[shift.id] || {})[pid];
      if (cross && cross.length > 0) {
        const lines = cross.map((c) => `• ${c.event_name} (${c.time}) — ${c.status === "confirmado" ? "confirmado" : "aceitou"}`).join("\n");
        if (!window.confirm(`⚠️ ${promoterMap[pid]?.name || "Promotor"} já tem horário sobreposto noutro evento:\n\n${lines}\n\nConfirmar mesmo assim?`)) return;
      }
    }
    let choice = {};
    if (status === "confirmado") {
      choice = await askMessage({ eventId, promoterId: pid, shiftId: shift.id, promoterName: `${promoterMap[pid]?.name || "Promotor"} · ${shift.name}`, kind: "confirmacao", title: "Confirmar no turno", confirmLabel: "Confirmar" });
      if (!choice) return false;
    }
    const { data } = await api.post(`/events/${eventId}/shifts/${shift.id}/candidates/${pid}`, { status, ...choice });
    if (status === "confirmado") {
      const name = promoterMap[pid]?.name || "Promotor";
      const wa = data?.whatsapp || {};
      if (!choice.send_message) toast.success(`${name} confirmado`, { description: "Sem mensagem enviada ao promotor." });
      else if (wa.ok) toast.success(`${name} confirmado`, { description: "Confirmação em fila — será enviada por WhatsApp 📤" });
      else if (wa.detail === "skipped") toast.warning(`${name} confirmado`, { description: "WhatsApp não ligado — mensagem de confirmação não enviada." });
      else toast.warning(`${name} confirmado`, { description: `Confirmação por WhatsApp não enviada (${wa.detail || "erro"}).` });
    }
    loadShifts();
  };
  const removeCandidate = async (shift, pid) => { await api.delete(`/events/${eventId}/shifts/${shift.id}/candidates/${pid}`); loadShifts(); };

  const copyShiftLink = (shift, pid) => {
    const c = shift.candidates.find((x) => x.promoter_id === pid);
    if (!c?.token) { toast.error("Sem link ainda — reenvia o convite deste turno a este promotor."); return; }
    navigator.clipboard.writeText(`${window.location.origin}/p/${c.token}`);
    toast.success("Link copiado.");
  };
  const dispenseCandidate = async (shift, pid) => {
    // Removing a CONFIRMED promoter sends them to "Dispensados" AND notifies them
    // with the WhatsApp rejection message (if the provider is ready).
    const choice = await askMessage({ eventId, promoterId: pid, shiftId: shift.id, promoterName: `${promoterMap[pid]?.name || "Promotor"} · ${shift.name}`, kind: "dispensa", title: "Remover do turno", confirmLabel: "Remover" });
    if (!choice) return;
    await api.post(`/events/${eventId}/shifts/${shift.id}/candidates/${pid}`, { status: "dispensado", ...choice });
    loadShifts();
  };
  const resendShift = async (shift) => {
    setResending(true);
    try {
      const { data } = await api.post(`/events/${eventId}/shifts/${shift.id}/requests/resend`);
      if (data.whatsapp_enabled === false) { setShowWaGate(true); return; }
      if (data.count === 0) window.alert("Ninguém por responder neste turno.");
      else window.alert(`Convite reenviado a ${data.count} promotor(es) sem resposta.`);
      loadShifts();
    } finally { setResending(false); }
  };

  const allInShifts = useMemo(() => {
    const set = new Set();
    shifts.forEach((s) => s.candidates.forEach((c) => set.add(c.promoter_id)));
    return [...set];
  }, [shifts]);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="shifts-panel">
      {msgDialog}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-xl font-display font-bold text-zinc-950 inline-flex items-center gap-2"><Clock className="w-4 h-4 text-zinc-500" /> Turnos</h2>
          <div className="text-sm text-zinc-500 mt-0.5">Recrutamento por turno · a mesma pessoa pode ser atribuída a vários turnos</div>
        </div>
        {canEdit && <button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-shift-btn" className="inline-flex items-center gap-1.5 bg-zinc-950 hover:bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm font-semibold"><Plus className="w-3.5 h-3.5" /> Adicionar turno</button>}
      </div>

      {showForm && (
        <div className="border border-zinc-200 rounded-lg p-4 mb-4 bg-zinc-50/50" data-testid="shift-form">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome (ex: Receção)" data-testid="shift-name" className="px-3 py-2 rounded-lg border border-zinc-200 text-sm sm:col-span-2" />
            <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="shift-start" className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
            <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="shift-end" className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <label className="text-xs text-zinc-600">Vagas</label>
            <input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} data-testid="shift-capacity" className="w-20 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
            <button onClick={submitShift} data-testid="save-shift-btn" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"><Check className="w-3.5 h-3.5" /> {editing ? "Guardar" : "Adicionar"}</button>
            <button onClick={resetForm} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">Cancelar</button>
          </div>
        </div>
      )}

      {shifts.length === 0 ? (
        <div className="text-sm text-zinc-400 py-6 text-center border border-dashed border-zinc-200 rounded-lg">Sem turnos. Adiciona o primeiro turno acima.</div>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex items-center gap-2 flex-wrap border-b border-zinc-100 pb-3 mb-4">
            <button onClick={() => setActiveTab("all")} data-testid="shift-tab-all" className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeTab === "all" ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>Todos</button>
            {shifts.map((s) => (
              <button key={s.id} onClick={() => setActiveTab(s.id)} data-testid={`shift-tab-${s.id}`} className={`px-3 py-1.5 rounded-full text-sm font-medium ${activeTab === s.id ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
                {s.name} · {s.start_time || "?"}–{s.end_time || "?"} <span className={activeTab === s.id ? "text-zinc-300" : "text-zinc-500"}>({s.confirmed_count}/{s.capacity})</span>
              </button>
            ))}
          </div>

          {activeTab === "all" ? (
            <div className="space-y-4">
              <div className="divide-y divide-zinc-100 border border-zinc-100 rounded-lg overflow-hidden">
                {shifts.map((s) => (
                  <div key={s.id} data-testid={`shift-row-${s.id}`} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-zinc-900">{s.name}</div>
                      <div className="text-xs text-zinc-500">{formatTimeRange(s.start_time, s.end_time)}</div>
                    </div>
                    <div className={`text-sm font-semibold ${s.confirmed_count >= s.capacity ? "text-emerald-700" : "text-amber-700"}`}>{s.confirmed_count}/{s.capacity}</div>
                    <button onClick={() => setActiveTab(s.id)} className="text-xs font-semibold text-zinc-700 hover:underline px-2">Gerir</button>
                    {canEdit && <>
                      <button onClick={() => editShift(s)} data-testid={`edit-shift-${s.id}`} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600" title="Editar"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => duplicateShift(s)} data-testid={`duplicate-shift-${s.id}`} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600" title="Duplicar"><Copy className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteShift(s)} data-testid={`delete-shift-${s.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>}
                  </div>
                ))}
              </div>

              {allInShifts.length > 0 && (
                <div className="overflow-x-auto">
                  <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Resumo por pessoa</div>
                  <table className="w-full text-sm">
                    <thead className="text-xs text-zinc-500 border-b border-zinc-200">
                      <tr><th className="text-left py-2 pr-3">Promotor</th>{shifts.map((s) => <th key={s.id} className="text-left py-2 px-2">{s.name}</th>)}</tr>
                    </thead>
                    <tbody>
                      {allInShifts.map((pid) => (
                        <tr key={pid} className="border-b border-zinc-100">
                          <td className="py-2 pr-3 font-medium text-zinc-800">{promoterMap[pid]?.name || pid.slice(0, 6)}</td>
                          {shifts.map((s) => {
                            const c = s.candidates.find((x) => x.promoter_id === pid);
                            return <td key={s.id} className="py-2 px-2">{c ? <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_CLS[c.status]}`}>{STATUS_LABEL[c.status]}</span> : <span className="text-zinc-300">—</span>}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (() => {
            const s = shifts.find((x) => x.id === activeTab);
            if (!s) return null;
            return (
              <div className="space-y-4" data-testid={`shift-panel-${s.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-zinc-600">{s.name} · {formatTimeRange(s.start_time, s.end_time)} · <span className={`font-semibold ${s.confirmed_count >= s.capacity ? "text-emerald-700" : "text-amber-700"}`}>{s.confirmed_count}/{s.capacity} vagas</span></div>
                  {canEdit && s.candidates.some((c) => c.status === "contactado") && (
                    <button onClick={() => resendShift(s)} disabled={resending} data-testid={`shift-resend-${s.id}`} className="inline-flex items-center gap-1.5 border border-zinc-200 hover:bg-zinc-50 px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-60">
                      {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Reenviar aos sem resposta
                    </button>
                  )}
                </div>

                {/* Estados do turno (estilo recrutamento) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <ShiftList title="Confirmados" ids={byStatus(s, "confirmado")} promoterMap={promoterMap} testid={`col-confirmado-${s.id}`} overlapMap={overlapMap[s.id]}
                    onAdd={canEdit ? () => setAddShift(s) : null}
                    primary={canEdit ? { label: "Remover", Icon: X, cls: "border border-red-200 text-red-700 hover:bg-red-50", onClick: (pid) => dispenseCandidate(s, pid) } : null} />
                  <ShiftList title="Aceitaram (a confirmar)" ids={byStatus(s, "disponivel")} promoterMap={promoterMap} testid={`col-disponivel-${s.id}`} overlapMap={overlapMap[s.id]}
                    primary={canEdit ? { label: "Confirmar", Icon: Check, cls: "bg-emerald-600 hover:bg-emerald-700 text-white", onClick: (pid) => setStatus(s, pid, "confirmado") } : null}
                    secondary={canEdit ? { label: "Dispensar", Icon: X, onClick: (pid) => setStatus(s, pid, "dispensado") } : null} />
                  <ShiftList title="Sem resposta" ids={byStatus(s, "contactado")} promoterMap={promoterMap} testid={`col-contactado-${s.id}`} overlapMap={overlapMap[s.id]}
                    primary={canEdit ? { label: "Confirmar", Icon: Check, cls: "bg-emerald-600 hover:bg-emerald-700 text-white", onClick: (pid) => setStatus(s, pid, "confirmado") } : null}
                    tertiary={{ label: "Copiar link", Icon: Link2, onClick: (pid) => copyShiftLink(s, pid) }}
                    secondary={canEdit ? { label: "Dispensar", Icon: X, onClick: (pid) => setStatus(s, pid, "dispensado") } : null} />
                  <ShiftList title="Recusaram" ids={byStatus(s, "indisponivel")} promoterMap={promoterMap} testid={`col-indisponivel-${s.id}`} />
                  <ShiftList title="Dispensados" ids={byStatus(s, "dispensado")} promoterMap={promoterMap} testid={`col-dispensado-${s.id}`}
                    primary={canEdit ? { label: "Repor", Icon: Plus, cls: "border border-zinc-200 hover:bg-zinc-50", onClick: (pid) => setStatus(s, pid, "contactado") } : null} />
                </div>

                {/* Promotores recomendados (por turno) */}
                {canEdit && (() => {
                  const inShift = new Set(s.candidates.map((c) => c.promoter_id));
                  const q = (addSearch[s.id] || "").trim().toLowerCase();
                  const eligible = recommendations.filter((p) => !inShift.has(p.id) && (!q || p.name.toLowerCase().includes(q)));
                  const sel = selected[s.id] || [];
                  return (
                    <div className="border border-zinc-200 rounded-xl p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                        <div>
                          <div className="text-sm font-semibold text-zinc-900">Promotores recomendados</div>
                          <div className="text-xs text-zinc-500">{eligible.length} elegíveis · ordenados por score</div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => setSelected({ ...selected, [s.id]: eligible.slice(0, 50).map((p) => p.id) })} className="text-sm text-zinc-700 hover:underline">Selecionar todos</button>
                          <button onClick={() => setSelected({ ...selected, [s.id]: [] })} className="text-sm text-zinc-500 hover:text-zinc-900">Limpar</button>
                          <button onClick={() => openSend(s)} disabled={sel.length === 0 || sending} data-testid={`shift-send-${s.id}`} className="inline-flex items-center gap-1.5 bg-zinc-950 hover:bg-zinc-800 text-white px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Contactar {sel.length > 0 ? `(${sel.length})` : ""}
                          </button>
                        </div>
                      </div>
                      <div className="relative mb-3">
                        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input value={addSearch[s.id] || ""} onChange={(e) => setAddSearch({ ...addSearch, [s.id]: e.target.value })} placeholder="Pesquisar por nome…" data-testid={`shift-add-search-${s.id}`} className="w-full pl-9 pr-3 py-2 rounded-lg border border-zinc-200 text-sm" />
                      </div>
                      <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100 max-h-72 overflow-y-auto">
                        {eligible.length === 0 && <div className="px-3 py-4 text-center text-xs text-zinc-400">Sem promotores para mostrar. Quem já está neste turno não aparece aqui.</div>}
                        {eligible.slice(0, 50).map((p) => {
                          const busy = busyMap[p.id];
                          const checked = sel.includes(p.id);
                          return (
                            <label key={p.id} data-testid={`shift-pick-${s.id}-${p.id}`} className={`flex items-center gap-2 px-3 py-2 cursor-pointer ${checked ? "bg-zinc-50" : "hover:bg-zinc-50"}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleSel(s.id, p.id)} className="w-4 h-4" />
                              <span className="flex-1 text-sm text-zinc-800">{p.name}</span>
                              {busy && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" title={`Já confirmado em: ${busy.join(", ")}`} data-testid={`busy-flag-${p.id}`}><AlertTriangle className="w-3 h-3" /> outro evento hoje</span>}
                              {typeof p.score === "number" && <span className="text-xs text-zinc-400 tabular-nums">{p.score}</span>}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </>
      )}

      <WhatsappGateModal open={showWaGate} onClose={() => setShowWaGate(false)} />

      <AddConfirmedModal
        open={!!addShift}
        onClose={() => setAddShift(null)}
        promoters={promoters}
        excludeIds={new Set((addShift?.candidates || []).map((c) => c.promoter_id))}
        onAdd={(pid) => setStatus(addShift, pid, "confirmado")}
        title={addShift ? `Adicionar confirmado · ${addShift.name}` : "Adicionar confirmado"}
      />

      {sendModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4" data-testid="shift-send-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-zinc-950">Contactar promotores</h2>
              <button onClick={() => setSendModal(null)} className="p-1 rounded hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-zinc-600">Turno <span className="font-semibold text-zinc-900">{sendModal.name}</span> ({formatTimeRange(sendModal.start_time, sendModal.end_time)}) · a enviar a <span className="font-semibold text-zinc-900">{(selected[sendModal.id] || []).length}</span> promotor(es). Cada um recebe um link único deste turno.</div>
            {templates.length > 0 && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">Template</label>
                <select data-testid="shift-template-selector" value={selectedTemplate} onChange={(e) => applyTemplate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm bg-white">
                  <option value="">— Mensagem personalizada —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-zinc-700 uppercase tracking-wide">Mensagem</label>
              <textarea data-testid="shift-send-message" value={message} onChange={(e) => { setMessage(e.target.value); setSelectedTemplate(""); }} rows={7} className="w-full px-3 py-2 rounded-lg border border-zinc-200 text-sm font-mono" placeholder="Escreve a mensagem ou escolhe um template…" />
              <div className="text-[11px] text-zinc-500">Placeholders: {"{nome_curto}"}, {"{evento}"}, {"{data}"}, {"{horario}"}, {"{local}"}, {"{link}"} — o {"{horario}"} usa o horário deste turno.</div>
            </div>
            <div className="flex gap-3 pt-2 border-t border-zinc-100">
              <button onClick={doSend} disabled={sending} data-testid="shift-confirm-send" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />} Enviar convite
              </button>
              <button onClick={() => setSendModal(null)} className="px-5 py-2.5 rounded-lg border border-zinc-200 text-sm hover:bg-zinc-50">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
